var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Loader, Table, TableSize, InlineNotification } from '@ui';
import { Text } from '@ui/atoms/Text';
import { Tag } from '@ui/atoms/Tag';
import { TextInput } from '@ui/molecules/inputs/form/TextInput';
import { ConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { useDebouncedListingSearch } from '@hooks/listings';
import { DefaultDebouceDelay } from '@constants/configs';
import { translate } from '@utils/i18n';
import { getFiles, getDownloadUrl, deleteFile, lockFolder, unlockFolder, extractZip } from '../../services/CustomerFileDropService';
import { QUERY_KEYS } from '../../hooks/queryKeys';
import { UploadManager } from '../UploadManager';
import './FileListStyles.scss';
const namespace = 'features.settings.customer_file_drop';
const PAGE_SIZE = 50;
function bytesToHuman(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
const EXTRACT_STATUS_ICON = {
    queued: 'fa-clock',
    extracting: 'fa-spinner fa-spin',
    complete: 'fa-check',
    failed: 'fa-triangle-exclamation'
};
function ExtractStatusBadge({ item }) {
    if (!item.originalFilename.toLowerCase().endsWith('.zip') || !item.extractStatus)
        return null;
    const icon = EXTRACT_STATUS_ICON[item.extractStatus];
    const label = translate(`extract_status_${item.extractStatus}`, { namespace });
    const title = item.extractStatus === 'failed' && item.extractError ? item.extractError : label;
    return (React.createElement("span", { className: `file-list__extract-badge file-list__extract-badge--${item.extractStatus}`, title: title },
        React.createElement("i", { className: `fa-light ${icon}`, "aria-hidden": "true" }),
        label));
}
function FilenameCell({ item }) {
    return (React.createElement("span", null,
        React.createElement(Text, { variant: "body" }, item.originalFilename),
        item.extractedFromFilename ? (React.createElement(Text, { variant: "x-small", className: "file-list__extracted-from" }, translate('extracted_from', { namespace, name: item.extractedFromFilename }))) : null));
}
function SizeCell({ item }) {
    return React.createElement(Text, { variant: "body" }, bytesToHuman(item.byteSize));
}
function UploadedByCell({ item }) {
    var _a;
    return React.createElement(Text, { variant: "body" }, (_a = item.uploadedBy) !== null && _a !== void 0 ? _a : '—');
}
function UploadedAtCell({ item }) {
    return React.createElement(Text, { variant: "body" }, new Date(item.uploadedAt).toLocaleDateString());
}
function FileList({ folder, canUseDrop, canLockFolders, onBack, onFolderUpdated }) {
    var _a, _b, _c, _d, _e;
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const [retryingId, setRetryingId] = useState(null);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTogglingLock, setIsTogglingLock] = useState(false);
    const queryClient = useQueryClient();
    const commitSearch = useCallback((value) => {
        setSearch(value);
        setPage(1);
    }, []);
    const { searchInput, handleSearchChange } = useDebouncedListingSearch({
        setSearch: commitSearch,
        delayMs: DefaultDebouceDelay
    });
    const canMutate = canUseDrop && !folder.locked;
    const { data, isLoading, isError } = useQuery({
        queryKey: QUERY_KEYS.files(folder.id, page, search),
        queryFn: () => getFiles(folder.id, page, PAGE_SIZE, search || undefined),
        staleTime: 30000,
        refetchInterval: (queryData) => {
            var _a;
            const files = (_a = queryData === null || queryData === void 0 ? void 0 : queryData.files) !== null && _a !== void 0 ? _a : [];
            const hasActiveZip = files.some((f) => f.originalFilename.toLowerCase().endsWith('.zip') &&
                (f.extractStatus === 'queued' || f.extractStatus === 'extracting'));
            return hasActiveZip ? 3000 : false;
        }
    });
    const handleDownload = (fileId) => __awaiter(this, void 0, void 0, function* () {
        setDownloadingId(fileId);
        try {
            const { url } = yield getDownloadUrl(folder.id, fileId);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        catch (_a) {
            window.alert(translate('download_error', { namespace }));
        }
        finally {
            setDownloadingId(null);
        }
    });
    const handleConfirmDelete = () => __awaiter(this, void 0, void 0, function* () {
        if (!fileToDelete)
            return;
        setIsDeleting(true);
        try {
            yield deleteFile(folder.id, fileToDelete.id);
            setFileToDelete(null);
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'files', folder.id] });
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
        }
        catch (_a) {
            window.alert(translate('file_delete_error', { namespace }));
        }
        finally {
            setIsDeleting(false);
        }
    });
    const handleToggleLock = () => __awaiter(this, void 0, void 0, function* () {
        setIsTogglingLock(true);
        try {
            const updated = folder.locked ? yield unlockFolder(folder.id) : yield lockFolder(folder.id);
            onFolderUpdated(updated);
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
        }
        catch (_a) {
            window.alert(translate(folder.locked ? 'folder_unlock_error' : 'folder_lock_error', { namespace }));
        }
        finally {
            setIsTogglingLock(false);
        }
    });
    const handleExtract = (fileId, isRetry) => __awaiter(this, void 0, void 0, function* () {
        setRetryingId(fileId);
        try {
            yield extractZip(folder.id, fileId);
            void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'files', folder.id] });
        }
        catch (_a) {
            window.alert(translate(isRetry ? 'extract_retry_error' : 'extract_zip_error', { namespace }));
        }
        finally {
            setRetryingId(null);
        }
    });
    const handleUploadsComplete = () => {
        void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'files', folder.id] });
        void queryClient.invalidateQueries({ queryKey: ['customer_file_drop', 'folders'] });
    };
    const files = (_a = data === null || data === void 0 ? void 0 : data.files) !== null && _a !== void 0 ? _a : [];
    const meta = data === null || data === void 0 ? void 0 : data.meta;
    const columns = useMemo(() => [
        {
            dataKey: 'id',
            label: translate('column_id', { namespace }),
            renderer: ({ item }) => React.createElement(Text, { variant: "body" }, item.id)
        },
        {
            dataKey: 'originalFilename',
            label: translate('column_name', { namespace }),
            columnType: 'primary',
            renderer: FilenameCell
        },
        {
            dataKey: 'byteSize',
            label: translate('column_size', { namespace }),
            align: 'right',
            renderer: SizeCell
        },
        {
            dataKey: 'uploadedBy',
            label: translate('column_uploaded_by', { namespace }),
            renderer: UploadedByCell
        },
        {
            dataKey: 'uploadedAt',
            label: translate('column_uploaded_at', { namespace }),
            renderer: UploadedAtCell
        },
        {
            dataKey: 'extractStatus',
            label: translate('column_extract_status', { namespace }),
            renderer: ExtractStatusBadge
        },
        {
            dataKey: 'actions',
            label: translate('column_actions', { namespace }),
            columnType: 'action',
            renderer: ({ item }) => (React.createElement("div", { className: "file-list__actions" },
                React.createElement(Button, { variant: "secondary", onClick: () => {
                        handleDownload(item.id).catch(() => undefined);
                    }, disabled: downloadingId === item.id },
                    React.createElement("i", { className: "fa-light fa-download", "aria-hidden": "true" }),
                    translate('download', { namespace })),
                canMutate && item.originalFilename.toLowerCase().endsWith('.zip') && !item.extractStatus ? (React.createElement(Button, { variant: "secondary", onClick: () => {
                        handleExtract(item.id, false).catch(() => undefined);
                    }, disabled: retryingId === item.id, "aria-label": translate('extract_zip', { namespace }), "data-testid": `extract-zip-${item.id}` },
                    React.createElement("i", { className: "fa-light fa-box-open", "aria-hidden": "true" }))) : null,
                canMutate && item.extractStatus === 'failed' && item.originalFilename.toLowerCase().endsWith('.zip') ? (React.createElement(Button, { variant: "secondary", onClick: () => {
                        handleExtract(item.id, true).catch(() => undefined);
                    }, disabled: retryingId === item.id, "aria-label": translate('extract_retry', { namespace }), "data-testid": `retry-extract-${item.id}` },
                    React.createElement("i", { className: "fa-light fa-rotate-right", "aria-hidden": "true" }))) : null,
                canMutate ? (React.createElement(Button, { variant: "secondary", onClick: () => setFileToDelete(item), "aria-label": translate('file_delete_confirm', {
                        namespace,
                        name: item.originalFilename
                    }), "data-testid": `delete-file-${item.id}` },
                    React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" }))) : null))
        }
    ], [downloadingId, retryingId, canMutate]);
    return (React.createElement("div", { className: "file-list", "data-testid": "file-list-panel" },
        React.createElement("div", { className: "file-list__header" },
            React.createElement(Button, { variant: "secondary", onClick: onBack },
                React.createElement("i", { className: "fa-light fa-arrow-left", "aria-hidden": "true" }),
                translate('back_to_folders', { namespace })),
            React.createElement("div", { className: "file-list__title-row" },
                React.createElement(Text, { variant: "large", as: "h2" }, translate('files_heading', { namespace, folder: folder.name })),
                React.createElement(Text, { variant: "body", className: "file-list__folder-id" },
                    translate('column_id', { namespace }),
                    " ",
                    folder.id),
                folder.locked ? React.createElement(Tag, { label: translate('locked_badge', { namespace }), variant: "gray" }) : null,
                canLockFolders ? (React.createElement(Button, { variant: "secondary", onClick: () => void handleToggleLock(), disabled: isTogglingLock, "data-testid": folder.locked ? 'unlock-folder-button' : 'lock-folder-button' },
                    React.createElement("i", { className: `fa-light ${folder.locked ? 'fa-lock-open' : 'fa-lock'}`, "aria-hidden": "true" }),
                    translate(folder.locked ? 'unlock_folder' : 'lock_folder', { namespace }))) : null)),
        folder.locked ? (React.createElement(InlineNotification, { variant: "info", title: translate('folder_locked_title', { namespace }), children: React.createElement("span", null,
                translate('folder_locked_hint', { namespace }),
                folder.lockedBy ? ` ${translate('folder_locked_by', { namespace, name: folder.lockedBy })}` : '') })) : null,
        canMutate && React.createElement(UploadManager, { folderId: folder.id, onUploadsComplete: handleUploadsComplete }),
        React.createElement(TextInput, { id: "customer-file-drop-file-search", label: translate('search_files', { namespace }), value: searchInput, onChange: (e) => handleSearchChange(e.target.value) }),
        isLoading && (React.createElement("div", { className: "file-list__loading" },
            React.createElement(Loader, { size: "48" }))),
        isError && (React.createElement("div", { className: "file-list__error" },
            React.createElement(Text, { variant: "body" }, translate('loading_error', { namespace })))),
        !isLoading && !isError && files.length === 0 && (React.createElement("div", { className: "file-list__empty" },
            React.createElement(Text, { variant: "body" }, translate('no_files', { namespace })))),
        files.length > 0 && (React.createElement("div", { "data-testid": "file-table" },
            React.createElement(Table, { data: files, columns: columns, size: TableSize.Full, variant: "listing", getItemId: (file) => file.id, onPageChange: setPage, serverCurrentPage: (_b = meta === null || meta === void 0 ? void 0 : meta.currentPage) !== null && _b !== void 0 ? _b : page, serverTotalPages: (_c = meta === null || meta === void 0 ? void 0 : meta.totalPages) !== null && _c !== void 0 ? _c : 1, serverTotalItems: (_d = meta === null || meta === void 0 ? void 0 : meta.totalCount) !== null && _d !== void 0 ? _d : files.length, pageSize: (_e = meta === null || meta === void 0 ? void 0 : meta.perPage) !== null && _e !== void 0 ? _e : PAGE_SIZE }))),
        React.createElement(ConfirmDialog, { isOpen: fileToDelete != null, title: translate('delete_file_title', { namespace }), description: fileToDelete ? translate('file_delete_confirm', { namespace, name: fileToDelete.originalFilename }) : '', confirmLabel: translate('delete_file_confirm', { namespace }), confirmVariant: "danger", cancelLabel: translate('cancel', { namespace: 'features.commons' }), loading: isDeleting, onConfirm: () => void handleConfirmDelete(), onCancel: () => setFileToDelete(null) })));
}
export default FileList;
